import {
  faCheck,
  faCircleUser,
  faGlobe,
  faHeadset,
  faRightFromBracket,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Center,
  Group,
  Menu,
  Text,
  rem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ErrorBoundary } from "@sentry/react";
import {
  AnchorLink,
  ErrorComponent,
  HealthCheck,
  MenuItemLink,
} from "@sweco-se/reality-frontend";
import { Header, SwecoLogo } from "@sweco/theme-mantine";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  HeadContent,
  Outlet,
  createRootRouteWithContext,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { type FC, type ReactNode, useState } from "react";
import { useAuth } from "react-oidc-context";

import { DocsAside } from "@/components/docs/DocsAside.tsx";
import { DocsNavbar } from "@/components/docs/DocsNavbar.tsx";
import { ActiveNewsRows } from "@/components/news/ActiveNewsRows.tsx";
import { openSupportTicketModal } from "@/components/zammad/CreateZammadTicketModal.tsx";
import { FEATURES } from "@/constants.ts";
import { useIsAdmin } from "@/hooks/useIsAdmin.ts";
import { useLocale } from "@/locales";
import type { RouterContext } from "@/router.ts";

export const AppErrorBoundary: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const location = useLocation();

  return (
    <ErrorBoundary
      key={location.pathname}
      fallback={({ error }) => <ErrorComponent error={error} />}
    >
      {children}
    </ErrorBoundary>
  );
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Component,
  head: () => ({
    meta: [
      {
        title: "Sweco REALity",
      },
    ],
  }),
});

function Component() {
  const { user, signoutSilent } = useAuth();
  const { locale, setLocale } = useLocale();
  const isAdmin = useIsAdmin();

  const { t } = useLingui();

  const [showTestWarning, setShowTestWarning] = useState(
    (window as any).REALITY_DATA_ENVIRONMENT === "test",
  );

  const location = useLocation();
  const isDocs = location.pathname.startsWith("/docs");
  const [navbarOpened, { toggle: toggleNavbar }] = useDisclosure();
  const headerHeight = showTestWarning ? 100 : 60;

  return (
    <>
      <HeadContent />
      <AppShell
        header={{ height: headerHeight }}
        navbar={
          isDocs
            ? {
                width: 280,
                breakpoint: "sm",
                collapsed: { mobile: !navbarOpened },
              }
            : undefined
        }
        aside={
          isDocs
            ? {
                width: 260,
                breakpoint: "md",
                collapsed: { mobile: true, desktop: false },
              }
            : undefined
        }
        padding="md"
      >
        <Header
          light={!showTestWarning}
          bg={showTestWarning ? "alert" : undefined}
          style={showTestWarning ? { borderBottom: "none" } : undefined}
          h={60}
        >
          <Group>
            {isDocs && (
              <Burger
                opened={navbarOpened}
                onClick={toggleNavbar}
                size="sm"
                hiddenFrom="sm"
                color={showTestWarning ? undefined : "black"}
                aria-label="Toggle documentation navigation"
              />
            )}
            <SwecoLogo black={showTestWarning ? undefined : true} size={28} />
            <Text variant="preamble">REALity</Text>

            {FEATURES.includes("property-search") && (
              <AnchorLink ml={"lg"} to="/property/search">
                <Text>
                  <Trans>Property Search</Trans>
                </Text>
              </AnchorLink>
            )}
            {FEATURES.includes("extraction") && (
              <AnchorLink to="/downloads">
                <Text>
                  <Trans>Downloads</Trans>
                </Text>
              </AnchorLink>
            )}
            <AnchorLink to="/docs">
              <Text>
                <Trans>Guides & Documentation</Trans>
              </Text>
            </AnchorLink>
          </Group>
          <Group>
            {user?.profile.email?.endsWith("@sweco.se") && (
              <AnchorLink to="/projects">
                <Text>
                  <Trans>Projects</Trans>
                </Text>
              </AnchorLink>
            )}
            {isAdmin && (
              <Menu
                trigger="hover"
                transitionProps={{ exitDuration: 0 }}
                withinPortal
                withArrow
                offset={11}
                arrowOffset={0}
              >
                <Menu.Target>
                  <Text>
                    <Trans>Admin</Trans>
                  </Text>
                </Menu.Target>
                <Menu.Dropdown>
                  <MenuItemLink to="/admin/news">
                    <Trans>News</Trans>
                  </MenuItemLink>
                  <MenuItemLink to="/admin/products">
                    <Trans>Products</Trans>
                  </MenuItemLink>
                  <MenuItemLink to="/admin/sources">
                    <Trans>Sources</Trans>
                  </MenuItemLink>
                  <MenuItemLink to="/admin/clients">
                    <Trans>Clients</Trans>
                  </MenuItemLink>
                  <MenuItemLink to="/transactions">
                    <Trans>Transactions</Trans>
                  </MenuItemLink>
                  <MenuItemLink to="/customers">
                    <Trans>Customers</Trans>
                  </MenuItemLink>
                </Menu.Dropdown>
              </Menu>
            )}
            <HealthCheck />
            <Menu
              trigger="hover"
              transitionProps={{ exitDuration: 0 }}
              withinPortal
              withArrow
              offset={11}
              arrowOffset={0}
            >
              <Menu.Target>
                <FontAwesomeIcon
                  icon={faCircleUser}
                  size="xl"
                  style={{ marginLeft: rem(5), marginRight: rem(5) }}
                />
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Sub>
                  <Menu.Sub.Target>
                    <Menu.Sub.Item
                      leftSection={<FontAwesomeIcon icon={faGlobe} />}
                    >
                      <Trans>Language</Trans>
                    </Menu.Sub.Item>
                  </Menu.Sub.Target>
                  <Menu.Sub.Dropdown>
                    <Menu.Item
                      onClick={() => setLocale("en")}
                      leftSection={
                        locale === "en" && <FontAwesomeIcon icon={faCheck} />
                      }
                    >
                      English
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => setLocale("sv")}
                      leftSection={
                        locale === "sv" && <FontAwesomeIcon icon={faCheck} />
                      }
                    >
                      Svenska
                    </Menu.Item>
                  </Menu.Sub.Dropdown>
                </Menu.Sub>
                <Menu.Item style={{ cursor: "inherit" }}>
                  {user?.profile.email ?? user?.profile.sub}
                </Menu.Item>
                <Menu.Item
                  leftSection={<FontAwesomeIcon icon={faHeadset} />}
                  onClick={openSupportTicketModal}
                >
                  <Trans>Contact support</Trans>
                </Menu.Item>
                <Menu.Item
                  onClick={() => signoutSilent()}
                  leftSection={<FontAwesomeIcon icon={faRightFromBracket} />}
                >
                  <Trans>Logout</Trans>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <Burger size="sm" hiddenFrom="xs" color="white" />
          </Group>
        </Header>
        {showTestWarning ? (
          <>
            <Center
              bg="alert"
              h={40}
              c="white"
              pos="fixed"
              top={60}
              left={0}
              right={0}
              fz="sm"
              ta="center"
              px="md"
              style={{ zIndex: 100 }}
            >
              <Trans>
                This site is using testing data which may look real but often is
                incorrect or outdated. It is strictly prohibited to the use data
                from this site for any purpose other than testing of this site
                itself.
              </Trans>
              <ActionIcon
                onClick={() => setShowTestWarning(false)}
                ml="md"
                color="white"
                variant="transparent"
                title={t`Hide temporarily`}
              >
                <FontAwesomeIcon icon={faXmark} />
              </ActionIcon>
            </Center>
            <Box
              bd="5px solid alert"
              pos="fixed"
              inset={0}
              style={{ zIndex: 102, pointerEvents: "none" }}
            />
          </>
        ) : null}
        {isDocs && (
          <AppShell.Navbar p="md">
            <DocsNavbar />
          </AppShell.Navbar>
        )}
        {isDocs && (
          <AppShell.Aside p="md" bd="none">
            <DocsAside headerHeight={headerHeight} />
          </AppShell.Aside>
        )}
        <AppShell.Main
          maw={!isDocs ? { xl: "88em" } : undefined}
          px={!isDocs ? { sm: "xl" } : undefined}
          mx={!isDocs ? "auto" : undefined}
          mih={!isDocs ? "100vh" : undefined}
        >
          <ActiveNewsRows />
          <AppErrorBoundary>
            <Outlet />
          </AppErrorBoundary>
        </AppShell.Main>
      </AppShell>
      <TanStackRouterDevtools />
      <ReactQueryDevtools buttonPosition="bottom-right" />
    </>
  );
}
